import React, { useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { BookingForm } from './BookingForm';
import { DestinationCard } from './DestinationCard';
import { Play, Star, Crown, Shield, Gem, Phone, Mail, MapPin, Instagram, Facebook, Twitter } from 'lucide-react';
import { destinations, testimonials, experiences, membershipBenefits } from '../data/mock';

export const LuxuryLanding = () => {
  const scrollRef = useRef(null);
  const heroImage = "https://images.unsplash.com/photo-1758192838598-a1de4da5dcaf";
  const experienceImage = "https://images.unsplash.com/photo-1625513123245-fcb02d69ad12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxwcml2YXRlJTIwamV0JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzU4ODMyNDEwfDA&ixlib=rb-4.1.0&q=85";

  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('.fade-in-up');
      elements.forEach(element => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;
        
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('animate-fade-in-up');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollDestinations = (direction) => {
    const container = scrollRef.current;
    const scrollAmount = 370;
    container.scrollBy({ 
      left: direction === 'left' ? -scrollAmount : scrollAmount, 
      behavior: 'smooth' 
    });
  };

  return (
    <div className="min-h-screen bg-off-white">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50 bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-amber-400" />
              <h1 className="font-serif text-2xl text-white font-bold tracking-wide">
                Golden Voyage
              </h1>
            </div>
            <nav className="hidden md:flex items-center gap-8 text-white/90">
              <a href="#destinations" className="hover:text-amber-400 transition-colors duration-300">Destinations</a>
              <a href="#experiences" className="hover:text-amber-400 transition-colors duration-300">Experiences</a>
              <a href="#membership" className="hover:text-amber-400 transition-colors duration-300">Membership</a>
              <Button variant="outline" className="border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-navy transition-all duration-300">
                Contact
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center transform scale-105"
          style={{
            backgroundImage: `url(${heroImage})`
          }}
        />
        
        {/* Video Placeholder Overlay */}
        <div className="absolute inset-0 bg-navy/40 backdrop-blur-[1px]" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-6 cursor-pointer hover:scale-110 transition-transform duration-300">
            <Play className="w-12 h-12 text-white" />
          </div>
          <p className="text-white/80 text-sm text-center mt-3">Watch Our Story</p>
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          <h1 className="font-serif text-6xl md:text-8xl text-white mb-6 leading-tight animate-fade-in-up">
            Experience Travel
            <span className="block text-amber-400">Beyond Luxury</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 font-light max-w-3xl mx-auto leading-relaxed">
            Exclusive journeys designed for those who demand more
          </p>
          
          {/* Booking Form */}
          <div className="max-w-4xl mx-auto">
            <BookingForm />
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-1 h-16 bg-gradient-to-b from-amber-400 to-transparent rounded-full" />
        </div>
      </section>

      {/* Featured Destinations */}
      <section id="destinations" className="py-24 bg-off-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="font-serif text-5xl md:text-6xl text-navy mb-6">
              Curated 
              <span className="text-amber-400"> Destinations</span>
            </h2>
            <p className="text-navy/70 text-xl max-w-2xl mx-auto leading-relaxed">
              Handpicked luxury destinations where extraordinary experiences await
            </p>
          </div>
          
          <div className="relative">
            <div 
              ref={scrollRef}
              className="flex gap-8 overflow-x-auto scrollbar-hide pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {destinations.map((destination, index) => (
                <DestinationCard 
                  key={destination.id} 
                  destination={destination} 
                  index={index}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Experience Section */}
      <section id="experiences" className="py-24 bg-navy">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="fade-in-up">
              <h2 className="font-serif text-5xl md:text-6xl text-white mb-8 leading-tight">
                The Golden Voyage
                <span className="block text-amber-400">Experience</span>
              </h2>
              <p className="text-white/80 text-lg mb-8 leading-relaxed">
                We don't just plan trips—we craft legendary journeys. Every detail is meticulously curated to exceed the expectations of the world's most discerning travelers.
              </p>
              
              <div className="space-y-6">
                {experiences.map((experience, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="bg-amber-400/20 p-3 rounded-full flex-shrink-0">
                      <Gem className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">{experience.title}</h3>
                      <p className="text-white/70">{experience.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button className="mt-8 bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-yellow-500 hover:to-amber-400 text-navy font-semibold px-8 py-3 transition-all duration-300 transform hover:scale-105">
                Discover More
              </Button>
            </div>
            
            {/* Image */}
            <div className="fade-in-up">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={experienceImage}
                  alt="Luxury Travel Experience"
                  className="w-full h-[600px] object-cover transform hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-sand/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="font-serif text-5xl md:text-6xl text-navy mb-6">
              What Our Guests
              <span className="text-amber-400 block">Say</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={testimonial.id} className="fade-in-up bg-white/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <blockquote className="font-serif text-navy text-lg italic mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                <div>
                  <p className="font-semibold text-navy">{testimonial.author}</p>
                  <p className="text-navy/60 text-sm">{testimonial.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section id="membership" className="py-24 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-amber-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16 fade-in-up">
            <div className="inline-flex items-center gap-3 bg-amber-400/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
              <Crown className="w-6 h-6 text-amber-400" />
              <span className="text-amber-400 font-semibold tracking-wide">EXCLUSIVE MEMBERSHIP</span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl text-white mb-6">
              Golden Voyage
              <span className="text-amber-400 block">Circle</span>
            </h2>
            <p className="text-white/80 text-xl max-w-2xl mx-auto leading-relaxed">
              Join our exclusive membership for unparalleled access to the world's most coveted experiences
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {membershipBenefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-4 fade-in-up">
                  <div className="bg-amber-400/20 p-2 rounded-full flex-shrink-0">
                    <Shield className="w-5 h-5 text-amber-400" />
                  </div>
                  <p className="text-white">{benefit}</p>
                </div>
              ))}
            </div>
            
            <div className="text-center fade-in-up">
              <Button className="bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-yellow-500 hover:to-amber-400 text-navy font-bold px-12 py-4 text-lg transition-all duration-300 transform hover:scale-105">
                Apply for Membership
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy/95 border-t border-gold/20 py-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Logo & Description */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <Crown className="w-8 h-8 text-amber-400" />
                <h3 className="font-serif text-2xl text-white font-bold">Golden Voyage</h3>
              </div>
              <p className="text-white/70 mb-6 leading-relaxed">
                Crafting extraordinary luxury travel experiences for discerning guests worldwide. 
                Your journey to the extraordinary begins here.
              </p>
              <div className="flex gap-4">
                <Instagram className="w-6 h-6 text-amber-400 hover:text-white cursor-pointer transition-colors" />
                <Facebook className="w-6 h-6 text-amber-400 hover:text-white cursor-pointer transition-colors" />
                <Twitter className="w-6 h-6 text-amber-400 hover:text-white cursor-pointer transition-colors" />
              </div>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="#destinations" className="text-white/70 hover:text-amber-400 transition-colors">Destinations</a></li>
                <li><a href="#experiences" className="text-white/70 hover:text-amber-400 transition-colors">Experiences</a></li>
                <li><a href="#membership" className="text-white/70 hover:text-amber-400 transition-colors">Membership</a></li>
                <li><a href="#" className="text-white/70 hover:text-amber-400 transition-colors">About Us</a></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-white/70">
                  <Phone className="w-4 h-4" />
                  <span>+1 (555) 123-4567</span>
                </li>
                <li className="flex items-center gap-2 text-white/70">
                  <Mail className="w-4 h-4" />
                  <span>hello@goldenvoyage.com</span>
                </li>
                <li className="flex items-center gap-2 text-white/70">
                  <MapPin className="w-4 h-4" />
                  <span>Beverly Hills, CA</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-amber-400/20 mt-12 pt-8 text-center">
            <p className="text-white/60">&copy; 2024 Golden Voyage. All rights reserved. Luxury redefined.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};